import {Icon, Menu} from '@albert-io/atomic';
import {QuestionModelV3} from '@albert-io/models';
import {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import cx from 'classnames';
import {List} from 'immutable';
import React, {useMemo} from 'react';
import Select from 'react-select';
import {useShallow} from 'zustand/react/shallow';

import FillInTheBlankQuestionStore from 'client/components/QuestionTypes/FillInTheBlank/FillInTheBlankQuestion.store';
import {useQuestionTranslationStore} from 'client/components/QuestionTypes/shared/TranslationSection';
import {callTargetedAction} from 'client/framework';
import fillInTheBlankQuestionActions from 'client/QuestionTypes/FillInTheBlank/V2/FillInTheBlankQuestion.actions';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import forceUpdateOnStateChange from 'lib/hocs/forceUpdateOnStateChange';

import './fitb-dropdown.scss';

interface FitbItemProps {
  choices: List<any>;
  dropdownId: string;
  store: FillInTheBlankQuestionStore;
  isGuessSubmitted: boolean;
  isExplanation: boolean;
  question: QuestionModelV3;
  isFITBRestylingEnabled: boolean;
}

function FitbItem({
  choices,
  dropdownId,
  store,
  isGuessSubmitted,
  isExplanation,
  question,
  isFITBRestylingEnabled
}: FitbItemProps) {
  return isExplanation ? (
    <FitbPresentationalItem
      question={question}
      store={store}
      dropdownId={dropdownId}
      choices={choices}
      isFITBRestylingEnabled={isFITBRestylingEnabled}
    />
  ) : (
    <FitbDropdown
      choices={choices}
      dropdownId={dropdownId}
      store={store}
      isGuessSubmitted={isGuessSubmitted}
      question={question}
      isFITBRestylingEnabled={isFITBRestylingEnabled}
    />
  );
}

interface FitbDropdownProps {
  choices: List<any>;
  dropdownId: string;
  store: FillInTheBlankQuestionStore;
  isGuessSubmitted: boolean;
  question: QuestionModelV3;
  isFITBRestylingEnabled: boolean;
}

function FitbDropdown({
  choices,
  dropdownId,
  store,
  isGuessSubmitted,
  question,
  isFITBRestylingEnabled
}: FitbDropdownProps) {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );
  // Format dropdown options so they jive with `react-select`
  const options = useMemo(() => {
    return choices.toJS().map((choice) => ({
      value: choice.id,
      label: choice.value
    }));
  }, [choices]);

  const selectedChoiceId = store.getSelectedChoiceForDropdown(dropdownId) || undefined;

  const selectedValue =
    (selectedChoiceId && options.find(({value: choiceId}) => selectedChoiceId === choiceId)) ||
    null;

  // Temporary hack to make differently named meta keys work
  // eslint-disable-next-line no-underscore-dangle
  const rubricMetaKey = question.__legacy ? 'rubric' : 'question_rubric';
  const validResponse = isGuessSubmitted
    ? question.getIn(['meta', rubricMetaKey, 'validResponse'], null)
    : null;
  const isCorrectAnswer =
    validResponse && !validResponse.isEmpty() && validResponse.get(dropdownId) === selectedChoiceId;

  const placeholderMap = {
    restyled: {es: 'Elige...', en: 'Choose...'},
    legacy: {es: 'Selecciona una opción', en: 'Select Option'}
  };
  const styleKey = isFITBRestylingEnabled ? 'restyled' : 'legacy';
  const langKey = showTranslations ? 'es' : 'en';
  const placeholder = placeholderMap[styleKey][langKey];

  const iconMap = {
    correct: isFITBRestylingEnabled ? 'circle-check' : 'check',
    incorrect: isFITBRestylingEnabled ? 'circle-xmark' : 'times'
  };
  const correctIcon = iconMap.correct;
  const incorrectIcon = iconMap.incorrect;

  if (isFITBRestylingEnabled) {
    return (
      <Menu
        wrapperClassName='inline-flex'
        className={cx('fitb-menu-container', {
          'fitb-menu-container--has-selection': !!selectedValue,
          'fitb-menu-container--restyled': isFITBRestylingEnabled,
          'fitb-menu-container--has-correct-answer': isGuessSubmitted && isCorrectAnswer,
          'fitb-menu-container--has-incorrect-answer': isGuessSubmitted && !isCorrectAnswer
        })}
        closeOnItemClick
      >
        <Menu.Trigger>
          {(triggerProps) => (
            <div
              {...triggerProps}
              className={cx('fitb-menu-trigger', {
                'fitb-menu-trigger--disabled': isGuessSubmitted
              })}
            >
              <span className='fitb-menu-value'>
                {selectedValue ? (
                  <MarkdownRendererV2 text={selectedValue.label} />
                ) : (
                  <span className='fitb-menu-placeholder'>{placeholder}</span>
                )}
              </span>
              <div className='fitb-menu-indicators'>
                <Icon
                  className='fitb-menu-indicator'
                  {...(() => {
                    if (!isGuessSubmitted) {
                      return {icon: 'caret-down'};
                    }
                    return {
                      icon: (isCorrectAnswer ? correctIcon : incorrectIcon) as IconProp,
                      iconStyle: isFITBRestylingEnabled ? 'regular' : 'solid',
                      color: isCorrectAnswer ? 'positive' : 'negative'
                    };
                  })()}
                />
              </div>
            </div>
          )}
        </Menu.Trigger>
        <Menu.Content className='!max-w-fit'>
          {options.map((option) => (
            <Menu.Item
              key={option.value}
              onClick={() => {
                if (!isGuessSubmitted) {
                  callTargetedAction({
                    name: fillInTheBlankQuestionActions.SELECT_OPTION,
                    payload: {
                      choiceId: option.value,
                      dropdownId
                    },
                    targetStore: store.getName()
                  });
                }
              }}
              selected={selectedValue?.value === option.value}
              disabled={isGuessSubmitted}
            >
              <MarkdownRendererV2 text={option.label} />
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu>
    );
  }

  return (
    <div data-testid='fitb-dropdown_select-option' className='fitb-select-wrapper'>
      <Select
        className={cx('fitb-select-container', {
          'fitb-select-container--has-selection': !!selectedValue,
          'fitb-select-container--restyled': isFITBRestylingEnabled,
          'fitb-select-container--has-correct-answer': isGuessSubmitted && isCorrectAnswer,
          'fitb-select-container--has-incorrect-answer': isGuessSubmitted && !isCorrectAnswer
        })}
        classNamePrefix='fitb-select'
        components={{
          DropdownIndicator: () => (
            <Icon
              className='fitb-select-indicator'
              {...(() => {
                if (!isGuessSubmitted) {
                  return {icon: 'caret-down'};
                }
                return {
                  icon: (isCorrectAnswer ? correctIcon : incorrectIcon) as IconProp,
                  iconStyle: isFITBRestylingEnabled ? 'regular' : 'solid',
                  color: isCorrectAnswer ? 'positive' : 'negative'
                };
              })()}
            />
          )
        }}
        formatOptionLabel={(option) => {
          return <MarkdownRendererV2 text={option.label} />;
        }}
        isDisabled={isGuessSubmitted}
        isSearchable={false}
        name='color'
        onChange={({value: choiceId}) => {
          callTargetedAction({
            name: fillInTheBlankQuestionActions.SELECT_OPTION,
            payload: {
              choiceId,
              dropdownId
            },
            targetStore: store.getName()
          });
        }}
        options={options}
        placeholder={placeholder}
        styles={{menu: (base) => ({...base, zIndex: 2})}}
        value={selectedValue}
      />
    </div>
  );
}

interface FitbPresentationalItemProps {
  choices: List<any>;
  dropdownId: string;
  store: FillInTheBlankQuestionStore;
  question: QuestionModelV3;
  isFITBRestylingEnabled: boolean;
}

function FitbPresentationalItem({
  question,
  store,
  dropdownId,
  choices,
  isFITBRestylingEnabled
}: FitbPresentationalItemProps) {
  const validResponse = question?.getValidResponse?.();
  const correctAnswers =
    !question || !validResponse || validResponse.isEmpty() ? store.validResponse : validResponse;
  const correctChoiceId = correctAnswers.get(dropdownId);
  const correctChoice = choices.find((choice) => choice.getId() === correctChoiceId)?.getValue();

  return (
    <div className={cx({'fitb-presentational-item--restyled': isFITBRestylingEnabled})}>
      <MarkdownRendererV2 className='fitb-presentational-item' text={correctChoice} />
      {isFITBRestylingEnabled && (
        <div className='fitb-select__indicators'>
          <Icon icon='circle-check' color='positive' iconStyle='regular' />
        </div>
      )}
    </div>
  );
}

export default forceUpdateOnStateChange(FitbItem);
